import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createCommentVNode as _createCommentVNode, withModifiers as _withModifiers, normalizeClass as _normalizeClass, normalizeStyle as _normalizeStyle, createTextVNode as _createTextVNode, vModelText as _vModelText, withKeys as _withKeys, withDirectives as _withDirectives } from "vue"

const _hoisted_1 = { class: "body-marker-container" }
const _hoisted_2 = ["src"]
const _hoisted_3 = ["onClick"]
const _hoisted_4 = { class: "marker-number" }
const _hoisted_5 = {
  key: 0,
  class: "marker-tooltip"
}
const _hoisted_6 = { class: "markers-list" }
const _hoisted_7 = { class: "markers-list-title" }
const _hoisted_8 = {
  key: 0,
  class: "markers-list-items"
}
const _hoisted_9 = { class: "marker-item-number" }
const _hoisted_10 = { class: "marker-item-description" }
const _hoisted_11 = ["onClick"]
const _hoisted_12 = {
  key: 1,
  class: "markers-list-empty"
}
const _hoisted_13 = { class: "modal-content" }
const _hoisted_14 = { class: "modal-title" }
const _hoisted_15 = { key: 0 }
const _hoisted_16 = { class: "modal-body" }
const _hoisted_17 = ["onKeydown"]
const _hoisted_18 = { class: "modal-footer" }
const _hoisted_19 = { class: "modal-footer-right" }

import { ref, computed, onMounted, onUnmounted } from "vue";
import skinImage from "@/assets/images/skin.jpg";

export interface Marker {
  id: string;
  number: number;
  x: number;
  y: number;
  description: string;
}

interface Props {
  modelValue?: Marker[];
  readonly?: boolean;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'BodyMarker',
  props: {
    modelValue: { default: () => [] },
    readonly: { type: Boolean, default: false }
  },
  emits: ["update:modelValue"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

const containerRef = ref<HTMLDivElement | null>(null);
const imageRef = ref<HTMLImageElement | null>(null);
const markers = ref<Marker[]>([...props.modelValue]);
const selectedMarker = ref<Marker | null>(null);
const showDescriptionModal = ref(false);
const newMarkerPosition = ref<{ x: number; y: number } | null>(null);
const descriptionInput = ref("");
const imageLoaded = ref(false);

// Генерация уникального ID
const generateId = (): string => {
  return `marker_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

// Получение следующего номера метки
const getNextNumber = (): number => {
  if (markers.value.length === 0) return 1;
  return Math.max(...markers.value.map((m) => m.number)) + 1;
};

// Конвертация координат клика в проценты относительно размера изображения
const getRelativePosition = (event: MouseEvent): { x: number; y: number } => {
  if (!imageRef.value) return { x: 0, y: 0 };

  const rect = imageRef.value.getBoundingClientRect();
  const x = ((event.clientX - rect.left) / rect.width) * 100;
  const y = ((event.clientY - rect.top) / rect.height) * 100;

  return { x, y };
};

// Обработка клика по изображению
const handleImageClick = (event: MouseEvent) => {
  if (props.readonly) return;

  const target = event.target as HTMLElement;
  if (
    target.classList.contains("marker-point") ||
    target.closest(".marker-point")
  ) {
    return;
  }

  const position = getRelativePosition(event);
  newMarkerPosition.value = position;
  descriptionInput.value = "";
  showDescriptionModal.value = true;
};

// Подтверждение добавления метки
const confirmAddMarker = () => {
  if (!newMarkerPosition.value) return;

  const newMarker: Marker = {
    id: generateId(),
    number: getNextNumber(),
    x: newMarkerPosition.value.x,
    y: newMarkerPosition.value.y,
    description: descriptionInput.value,
  };

  markers.value.push(newMarker);
  emit("update:modelValue", [...markers.value]);
  closeDescriptionModal();
};

// Закрытие модального окна
const closeDescriptionModal = () => {
  showDescriptionModal.value = false;
  newMarkerPosition.value = null;
  descriptionInput.value = "";
  selectedMarker.value = null;
};

// Выбор метки для редактирования
const selectMarker = (marker: Marker) => {
  if (props.readonly) return;
  selectedMarker.value = marker;
  descriptionInput.value = marker.description;
  showDescriptionModal.value = true;
};

// Обновление описания метки
const updateMarkerDescription = () => {
  if (!selectedMarker.value) return;

  const index = markers.value.findIndex(
    (m) => m.id === selectedMarker.value!.id,
  );
  if (index !== -1) {
    markers.value[index].description = descriptionInput.value;
    emit("update:modelValue", [...markers.value]);
  }
  closeDescriptionModal();
};

// Удаление метки
const deleteMarker = (markerId: string) => {
  markers.value = markers.value.filter((m) => m.id !== markerId);
  // Перенумерация оставшихся меток
  markers.value.forEach((m, index) => {
    m.number = index + 1;
  });
  emit("update:modelValue", [...markers.value]);
  closeDescriptionModal();
};

// Обработка подтверждения в модальном окне
const handleModalConfirm = () => {
  if (selectedMarker.value) {
    updateMarkerDescription();
  } else {
    confirmAddMarker();
  }
};

// Обработка нажатия Escape
const handleKeydown = (event: KeyboardEvent) => {
  if (event.key === "Escape" && showDescriptionModal.value) {
    closeDescriptionModal();
  }
};

onMounted(() => {
  document.addEventListener("keydown", handleKeydown);
});

onUnmounted(() => {
  document.removeEventListener("keydown", handleKeydown);
});

// Синхронизация с props.modelValue
const syncMarkers = () => {
  markers.value = [...props.modelValue];
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", {
      class: "image-wrapper",
      ref_key: "containerRef",
      ref: containerRef
    }, [
      _createElementVNode("img", {
        ref_key: "imageRef",
        ref: imageRef,
        src: _unref(skinImage),
        alt: "Схема тела",
        class: "body-image",
        onClick: handleImageClick,
        onLoad: _cache[0] || (_cache[0] = ($event: any) => (imageLoaded.value = true))
      }, null, 40, _hoisted_2),
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(markers.value, (marker) => {
        return (_openBlock(), _createElementBlock("div", {
          key: marker.id,
          class: _normalizeClass(["marker-point", { selected: selectedMarker.value?.id === marker.id }]),
          style: _normalizeStyle({
          left: `${marker.x}%`,
          top: `${marker.y}%`,
        }),
          onClick: _withModifiers(($event: any) => (selectMarker(marker)), ["stop"])
        }, [
          _createElementVNode("span", _hoisted_4, _toDisplayString(marker.number), 1),
          (marker.description)
            ? (_openBlock(), _createElementBlock("div", _hoisted_5, _toDisplayString(marker.description), 1))
            : _createCommentVNode("", true)
        ], 14, _hoisted_3))
      }), 128))
    ], 512),
    _createElementVNode("div", _hoisted_6, [
      _createElementVNode("h5", _hoisted_7, "Метки (" + _toDisplayString(markers.value.length) + "):", 1),
      (markers.value.length > 0)
        ? (_openBlock(), _createElementBlock("ul", _hoisted_8, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(markers.value, (marker) => {
              return (_openBlock(), _createElementBlock("li", {
                key: marker.id,
                class: "marker-item"
              }, [
                _createElementVNode("span", _hoisted_9, _toDisplayString(marker.number) + ".", 1),
                _createElementVNode("span", _hoisted_10, _toDisplayString(marker.description || "Без описания"), 1),
                (!__props.readonly)
                  ? (_openBlock(), _createElementBlock("button", {
                      key: 0,
                      class: "marker-item-delete",
                      onClick: ($event: any) => (deleteMarker(marker.id)),
                      title: "Удалить метку"
                    }, " × ", 8, _hoisted_11))
                  : _createCommentVNode("", true)
              ]))
            }), 128))
          ]))
        : (_openBlock(), _createElementBlock("div", _hoisted_12, " Нажмите на изображение, чтобы добавить метку "))
    ]),
    (showDescriptionModal.value)
      ? (_openBlock(), _createElementBlock("div", {
          key: 0,
          class: "modal-overlay",
          onClick: _withModifiers(closeDescriptionModal, ["self"])
        }, [
          _createElementVNode("div", _hoisted_13, [
            _createElementVNode("h4", _hoisted_14, [
              _createTextVNode(_toDisplayString(selectedMarker.value ? "Редактировать метку" : "Новая метка") + " ", 1),
              (!selectedMarker.value && newMarkerPosition.value)
                ? (_openBlock(), _createElementBlock("span", _hoisted_15, " №" + _toDisplayString(getNextNumber()), 1))
                : _createCommentVNode("", true)
            ]),
            _createElementVNode("div", _hoisted_16, [
              _cache[3] || (_cache[3] = _createElementVNode("label", { for: "markerDescription" }, "Описание:", -1)),
              _withDirectives(_createElementVNode("textarea", {
                id: "markerDescription",
                "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((descriptionInput).value = $event)),
                class: "form-control",
                rows: "3",
                placeholder: "Введите описание метки",
                onKeydown: _withKeys(_withModifiers(handleModalConfirm, ["ctrl"]), ["enter"]),
                autofocus: ""
              }, null, 40, _hoisted_17), [
                [_vModelText, descriptionInput.value]
              ])
            ]),
            _createElementVNode("div", _hoisted_18, [
              (selectedMarker.value)
                ? (_openBlock(), _createElementBlock("button", {
                    key: 0,
                    class: "btn btn-danger",
                    onClick: _cache[2] || (_cache[2] = ($event: any) => (deleteMarker(selectedMarker.value.id)))
                  }, " Удалить "))
                : _createCommentVNode("", true),
              _createElementVNode("div", _hoisted_19, [
                _createElementVNode("button", {
                  class: "btn btn-secondary",
                  onClick: closeDescriptionModal
                }, " Отмена "),
                _createElementVNode("button", {
                  class: "btn btn-primary",
                  onClick: handleModalConfirm
                }, _toDisplayString(selectedMarker.value ? "Сохранить" : "Добавить"), 1)
              ])
            ])
          ])
        ]))
      : _createCommentVNode("", true)
  ]))
}
}

})